import Tooltip from 'components/@extended/Tooltip';
import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { Button } from 'components/ui/button';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { buttonStyle } from './buttonStyle';
import PermissionButton from './PermissionButton';

interface Props {
    permissions?: string[];
    hasAuthority?: boolean;
    onClick: () => void;
    disabled?: boolean;
    /** Why the action cannot be taken, as a translation key. Shown on hover, also while the button is disabled. */
    tooltip?: string;
    children?: ReactNode;
}

/** In the create dialog's footer the main action: wider, and the whole row on the full-screen sheet. */
export const CREATE_DIALOG_MAIN_ACTION_CLASS = 'px-5 font-semibold max-sm:h-12 max-sm:flex-1 max-sm:text-base';

/**
 * A form's main action. It reads "Save" unless the form names it; in the create dialog, where it creates the
 * record, it reads "Create".
 *
 * A form that says why the action is unavailable passes `tooltip` rather than wrapping the button: the create
 * dialog's footer knows the main action by its being this component.
 */
export default function SaveButton({ permissions, hasAuthority, onClick, disabled, tooltip, children }: Props) {
    const inCreateDialog = useInCreateDialog();
    const label = children ?? <FormattedMessage id={inCreateDialog ? 'create-dialog.actions.create' : 'save'} />;
    const className = inCreateDialog ? CREATE_DIALOG_MAIN_ACTION_CLASS : undefined;
    const isPermissionGated = permissions !== undefined || hasAuthority !== undefined;

    const button = isPermissionGated ? (
        <PermissionButton
            permissions={permissions}
            hasAuthority={hasAuthority}
            color="secondary"
            variant="contained"
            size="small"
            className={className}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </PermissionButton>
    ) : (
        <Button
            {...buttonStyle({ variant: 'contained', color: 'secondary' }, className)}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </Button>
    );

    if (!tooltip) {
        return button;
    }
    // A disabled button takes no pointer events, so the wrapper is what is hovered. It takes the button's place
    // in the row, and none when the user may not see the button.
    return (
        <Tooltip text={tooltip}>
            <span className={cn('inline-flex empty:hidden', inCreateDialog && 'max-sm:flex-1')}>{button}</span>
        </Tooltip>
    );
}

// The create dialog's sheet footer picks a form's main action out of its actions by this name.
SaveButton.displayName = 'SaveButton';
